import { CheckIcon } from '@radix-ui/react-icons'
import { Flex } from '@radix-ui/themes'
import { DataTestIDs } from 'shared/constants'
import {
  CrownIcon,
  FlashIcon,
  SolanaAssetIcon,
  TetherAssetIcon
} from 'shared/icons'
import { NeonPanel } from 'shared/ui/neon'
import { formatToTether } from 'shared/utils/format-price'
import { RECOMMENDED_SOL_BALANCE } from 'solana-bc/sol-fee'
import type { WalletBalance } from '../types'
import { formatSol, formatSolAmount } from '../utils/format-sol'
import { getFundingState } from '../utils/get-funding-state'
import styles from '../neon-wallets.module.scss'

const GOALS = 2

interface GoalProps {
  isDone: boolean
  icon: React.ReactNode
  label: string
  value: string
}

const Goal: React.FC<GoalProps> = ({ isDone, icon, label, value }) => (
  <Flex
    align='center'
    gap='2'
    className={styles.goal}
    data-done={isDone}
  >
    <span className={styles.goalCheck}>
      {isDone ?
        <CheckIcon />
      : null}
    </span>
    <span className={styles.goalIcon}>{icon}</span>
    <span className={styles.goalLabel}>{label}</span>
    <span className={styles.goalValue}>{value}</span>
  </Flex>
)

interface Props {
  balance?: WalletBalance
}

/**
 * A small quest, in the spirit of Bull's Eye: two goals that light up as the
 * deposit lands (the balance arrives by subscription), then "Ready to play!".
 */
export const ReadyToPlay: React.FC<Props> = ({ balance }) => {
  const { sol, usdt, hasFuel, hasStake, isReady, progress } =
    getFundingState(balance)
  const doneCount = Number(hasFuel) + Number(hasStake)

  return (
    <NeonPanel
      glow={isReady ? 'green' : 'purple'}
      padding='1'
      gap='2'
      className={styles.quest}
      data-ready={isReady}
      data-testid={DataTestIDs.walletDepositReadiness}
    >
      <Flex
        align='center'
        justify='between'
        gap='2'
      >
        <Flex
          align='center'
          gap='2'
        >
          <span className={styles.questIcon}>
            {isReady ?
              <CrownIcon />
            : <FlashIcon />}
          </span>
          <span className={styles.questTitle}>
            {isReady ? 'Ready to play!' : 'Get ready to play'}
          </span>
        </Flex>
        <span className={styles.questCount}>
          {doneCount}/{GOALS}
        </span>
      </Flex>

      <span
        className={styles.questBar}
        role='progressbar'
        aria-label='Ready to play'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
      >
        <span style={{ width: `${progress * 100}%` }} />
      </span>

      <Goal
        isDone={hasFuel}
        icon={<SolanaAssetIcon />}
        label='SOL for network fees'
        value={
          hasFuel ?
            formatSol(sol)
          : `${formatSolAmount(sol)} / ${RECOMMENDED_SOL_BALANCE} SOL`
        }
      />
      <Goal
        isDone={hasStake}
        icon={<TetherAssetIcon />}
        label='USDT to play'
        value={`${formatToTether(Math.trunc(usdt * 100) / 100)} USDT`}
      />
    </NeonPanel>
  )
}
